import { EntityDialog, PropertyDialog, localText, type PropertyItem } from "@serenity-is/corelib";

/**
 * Shows field tips from localisation texts (Forms.{formKey}.{field}_Hint).
 * Serenity already uses that key as the caption tooltip. This applier copies a
 * tip onto a property item that has no [Hint] of its own, and marks the label
 * so the tooltip is visible. It never replaces a hint that the form already set.
 * Elements with data-field-tip="Pos.Return.Session" use the same key pattern
 * for screens that are not Serenity forms (POS Return).
 */

const applied = Symbol("fieldTips");

export function fieldTipKey(form: string, field: string): string {
    return "Forms." + form + "." + field + "_Hint";
}

export function lookupFieldTip(form: string, field: string): string {
    if (!form || !field)
        return "";
    const key = fieldTipKey(form, field);
    const value = localText(key, "");
    return value && value !== key ? value : "";
}

export function applyFieldTips(formKey: string, items: PropertyItem[] | null | undefined): void {
    if (!formKey || !items?.length)
        return;
    for (const item of items) {
        if (!item?.name || item.hint)
            continue;
        const tip = lookupFieldTip(formKey, item.name);
        if (tip)
            item.hint = tip;
    }
}

function markCaptionTips(root: ParentNode) {
    root.querySelectorAll<HTMLElement>(".field label.caption").forEach(label => {
        const title = label.getAttribute("title") || "";
        const caption = (label.textContent || "").replace("*", "").trim();
        if (title && caption && title !== caption)
            label.classList.add("s-field-tip");
    });
}

function applyDataTips(root: ParentNode) {
    root.querySelectorAll<HTMLElement>("[data-field-tip]").forEach(el => {
        const name = el.getAttribute("data-field-tip") || "";
        const dot = name.lastIndexOf(".");
        if (dot <= 0)
            return;
        const tip = lookupFieldTip(name.slice(0, dot), name.slice(dot + 1));
        if (tip && el.getAttribute("title") !== tip)
            el.setAttribute("title", tip);
    });
}

export function refreshFieldTips(root: ParentNode = document) {
    applyDataTips(root);
    markCaptionTips(root);
}

function formKeyOf(dialog: { getFormKey?: () => string }) {
    return typeof dialog?.getFormKey === "function" ? dialog.getFormKey() : "";
}

function patchPropertySource(proto: any) {
    if (!proto || proto[applied])
        return;
    proto[applied] = true;
    if (typeof proto.getPropertyItemsData === "function") {
        const original = proto.getPropertyItemsData;
        proto.getPropertyItemsData = function () {
            const data = original.apply(this, arguments);
            applyFieldTips(formKeyOf(this), data?.items);
            return data;
        };
    }
    if (typeof proto.getPropertyItemsDataAsync === "function") {
        const originalAsync = proto.getPropertyItemsDataAsync;
        proto.getPropertyItemsDataAsync = async function () {
            const data = await originalAsync.apply(this, arguments);
            applyFieldTips(formKeyOf(this), data?.items);
            return data;
        };
    }
    if (typeof proto.getPropertyItems === "function") {
        const originalItems = proto.getPropertyItems;
        proto.getPropertyItems = function () {
            const items = originalItems.apply(this, arguments);
            if (Array.isArray(items))
                applyFieldTips(formKeyOf(this), items);
            return items;
        };
    }
}

function install() {
    patchPropertySource(EntityDialog.prototype);
    patchPropertySource(PropertyDialog.prototype);

    let scheduled = false;
    const observer = new MutationObserver(() => {
        if (scheduled)
            return;
        scheduled = true;
        queueMicrotask(() => {
            scheduled = false;
            refreshFieldTips(document);
        });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    refreshFieldTips(document);
}

if (document.body)
    install();
else
    document.addEventListener("DOMContentLoaded", install);
